import { BaseComponent } from '../../components/base-component'
import { Nullable } from '../../types/common'
import { isMobile } from '../../utils/check-media'
import { debounce } from '../../utils/debounce'
import { setStyleVariable } from '../../utils/dom'
import { FloatElement } from '../../components/float-element/float-element'
import { BodyElement } from 'src/scripts/components/body-element/body-element'
import { $el, $list, whenDefined } from 'src/scripts/utils/utils'

const HEADER_DROPDOWN_SELECTOR = '[data-header-dropdown]'
const SECTION_SELECTOR = '.shopify-section-header'
const BODY_ELEMENT_SELECTOR = 'body-element'

export class HeaderComponent extends BaseComponent {
  lastScrollPosition: number = 0
  headerSectionBottom: number
  resizeObserver: ResizeObserver
  isHidden: boolean
  lastBottom: number
  lastHeight: number

  mountComponent(): void {
    const dropdowns: FloatElement[] = $list(HEADER_DROPDOWN_SELECTOR, this)

    this.resizeObserver = new ResizeObserver(this.handleResize)
    this.resizeObserver.observe(this)

    this.setSticky()
    this.updatePosition()
    this.addListener(window, 'scroll', this.handleScroll)
    dropdowns.forEach(dropdown => {
      customElements.upgrade(dropdown) // https://developer.mozilla.org/en-US/docs/Web/API/CustomElementRegistry/upgrade
      whenDefined('float-element').then(() => {
        dropdown.on('show', this.handleDropdownShow)
        dropdown.on('hide', this.handleDrodpownHide)
      })
    })
  }

  unmountComponent(): void {
    const dropdowns: FloatElement[] = $list(HEADER_DROPDOWN_SELECTOR, this)

    dropdowns.forEach(dropdown => {
      customElements.upgrade(dropdown) // https://developer.mozilla.org/en-US/docs/Web/API/CustomElementRegistry/upgrade
      whenDefined('float-element').then(() => {
        dropdown.off('show', this.handleDropdownShow)
        dropdown.off('hide', this.handleDrodpownHide)
      })
    })
    this.resizeObserver.disconnect()
  }

  handleResize = debounce((): void => {
    this.setHidden(false)
    this.updatePosition()

    const rect = this.section.getBoundingClientRect()

    this.headerSectionBottom = rect.bottom
  }, 100)

  handleScroll = debounce((): void => {
    if (isMobile()) {
      this.updateVisibleAfterScroll()
    }

    this.setScrollStatus()
  }, 50)

  handleDrodpownHide = (): void => {
    this.classList.remove('header--light-bordered')
  }

  handleDropdownShow = ({ element }): void => {
    const nav = $el('.mega-menu__nav, .header__wide-menu', element)

    if (nav) {
      this.classList.add('header--light-bordered')
    }
  }

  setScrollStatus(): void {
    if (!this.section) {
      return
    }

    const savedScrollPosition = Math.abs(
      parseInt(document.body.style.top || '0'),
    )
    const scrollPosition = window.scrollY || savedScrollPosition

    this.section.classList.toggle(
      'scrolled',
      scrollPosition > this.headerSectionBottom,
    )
  }

  updateVisibleAfterScroll(): void {
    const bodyElement: BodyElement = $el(BODY_ELEMENT_SELECTOR)

    if (!this.section) {
      return
    }

    if (bodyElement.isFixed) {
      return
    }

    const threshold = 36
    const scrollDistance = window.scrollY - this.lastScrollPosition
    const isScrollUp = scrollDistance < 0
    const isScrollDown = scrollDistance > 0
    const isUnderHeader = window.scrollY > this.headerSectionBottom

    if (isScrollUp) {
      const showOnScrollUp = scrollDistance + threshold > 0
      this.setHidden(this.isHidden && showOnScrollUp && isUnderHeader)
    } else if (isScrollDown) {
      const hideOnScrollDown = scrollDistance > threshold
      this.setHidden(hideOnScrollDown && isUnderHeader)
    }

    this.lastScrollPosition = window.scrollY
  }

  setHidden(isHidden: boolean): void {
    if (!this.section) {
      return
    }

    this.isHidden = isHidden
    this.section.setAttribute('aria-hidden', isHidden ? 'true' : 'false')
  }

  setSticky(): void {
    if (!this.section) {
      return
    }
    const isSticky = this.hasAttribute('is-sticky')

    this.section.setAttribute('mobile-sticky', '')
    this.section.toggleAttribute('desktop-sticky', isSticky)
    document.body.toggleAttribute('header-desktop-sticky', isSticky)
  }

  updatePosition(): void {
    if (!this.section) {
      return
    }

    const rect = this.getBoundingClientRect()

    if (this.lastHeight !== rect.height) {
      setStyleVariable('header-height', `${rect.height}px`)
      this.lastHeight = rect.height
    }

    if (this.lastBottom !== rect.bottom) {
      setStyleVariable('header-bottom-side', `${rect.bottom}px`)
      this.lastBottom = rect.bottom
    }
  }

  get section(): Nullable<HTMLElement> {
    return $el(SECTION_SELECTOR)
  }
}
